class DezhouHeader extends HTMLElement {
  constructor() {
    super();
    this.openDetails = null;
    this.lastFocusedElement = null;
    this.hoverCloseTimer = null;
    this.mediaQuery = window.matchMedia('(max-width: 960px)');
  }

  connectedCallback() {
    this.drawer = this.querySelector('[data-dz-mobile-drawer]');
    this.menuToggle = this.querySelector('[data-dz-menu-toggle]');
    this.modal = this.querySelector('[data-dz-quote-modal]');
    this.modalDialog = this.querySelector('[data-dz-quote-dialog]');
    this.modalClose = this.querySelector('[data-dz-quote-close]');
    this.detailsItems = [...this.querySelectorAll('[data-dz-menu-details]')];
    this.scrollTarget = this.resolveScrollTarget();

    this.updateHeaderState();
    this.updateHeaderHeight();
    this.bindNavigation();
    this.bindDrawer();
    this.bindModal();

    this.scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    document.addEventListener('keydown', this.handleDocumentKeyDown);

    if (this.querySelector('[data-dz-form-state="success"], [data-dz-form-state="error"]')) {
      window.requestAnimationFrame(() => this.openModal(this.querySelector('[data-dz-quote-open]')));
    }
  }

  disconnectedCallback() {
    this.scrollTarget?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
  }

  resolveScrollTarget() {
    const pageWrapper = document.querySelector('.page-wrapper');
    return this.mediaQuery.matches || !pageWrapper ? window : pageWrapper;
  }

  getScrollTop() {
    return this.scrollTarget === window
      ? window.scrollY || document.documentElement.scrollTop
      : this.scrollTarget.scrollTop;
  }

  handleScroll = () => {
    window.requestAnimationFrame(() => this.updateHeaderState());
  };

  handleResize = () => {
    const nextScrollTarget = this.resolveScrollTarget();
    if (nextScrollTarget !== this.scrollTarget) {
      this.scrollTarget.removeEventListener('scroll', this.handleScroll);
      this.scrollTarget = nextScrollTarget;
      this.scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
    }
    this.updateHeaderState();
    this.updateHeaderHeight();
    if (!this.mediaQuery.matches) this.closeDrawer();
  };

  updateHeaderState() {
    this.toggleAttribute('data-scrolled', this.getScrollTop() > 56);
  }

  updateHeaderHeight() {
    const height = Math.round(this.getBoundingClientRect().height);
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
  }

  bindNavigation() {
    this.detailsItems.forEach((details) => {
      const summary = details.querySelector('summary');
      const open = () => {
        if (this.mediaQuery.matches) return;
        window.clearTimeout(this.hoverCloseTimer);
        this.closeMenus(details);
        details.open = true;
        this.loadGuideImages(details);
        summary?.setAttribute('aria-expanded', 'true');
        this.openDetails = details;
      };
      const scheduleClose = () => {
        if (this.mediaQuery.matches) return;
        window.clearTimeout(this.hoverCloseTimer);
        this.hoverCloseTimer = window.setTimeout(() => this.closeMenus(), 140);
      };

      details.addEventListener('pointerenter', open);
      details.addEventListener('pointerleave', scheduleClose);
      details.addEventListener('toggle', () => {
        summary?.setAttribute('aria-expanded', String(details.open));
        if (details.open) {
          this.closeMenus(details);
          this.loadGuideImages(details);
          this.openDetails = details;
        } else if (this.openDetails === details) {
          this.openDetails = null;
        }
      });
    });
  }

  closeMenus(except = null) {
    this.detailsItems.forEach((details) => {
      if (details !== except) {
        details.open = false;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }
    });
    if (!except) this.openDetails = null;
  }

  loadGuideImages(details) {
    details.querySelectorAll('.dz-header__guide-image[loading="lazy"]').forEach((image) => {
      if (image.complete && image.naturalWidth > 0) return;
      const source = image.currentSrc || image.src;
      image.loading = 'eager';
      if (source) image.src = source;
    });
  }

  bindDrawer() {
    this.menuToggle?.addEventListener('click', () => {
      const shouldOpen = this.drawer?.getAttribute('aria-hidden') !== 'false';
      if (shouldOpen) this.openDrawer();
      else this.closeDrawer();
    });

    this.drawer?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeDrawer());
    });
  }

  openDrawer() {
    if (!this.drawer || !this.menuToggle) return;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.menuToggle.setAttribute('aria-expanded', 'true');
    this.menuToggle.setAttribute('aria-label', 'Close menu');
    this.setAttribute('data-drawer-open', '');
    document.body.classList.add('dz-header-scroll-lock');
  }

  closeDrawer() {
    if (!this.drawer || !this.menuToggle) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.menuToggle.setAttribute('aria-expanded', 'false');
    this.menuToggle.setAttribute('aria-label', 'Open menu');
    this.removeAttribute('data-drawer-open');
    this.closeMenus();
    if (!this.modal?.classList.contains('is-open')) document.body.classList.remove('dz-header-scroll-lock');
  }

  bindModal() {
    this.querySelectorAll('[data-dz-quote-open]').forEach((button) => {
      button.addEventListener('click', () => this.openModal(button));
    });
    this.modalClose?.addEventListener('click', () => this.closeModal());
    this.modal?.addEventListener('pointerdown', (event) => {
      if (event.target === this.modal) this.closeModal();
    });
  }

  openModal(trigger) {
    if (!this.modal) return;
    this.lastFocusedElement = trigger || document.activeElement;
    this.closeMenus();
    this.closeDrawer();
    this.modal.inert = false;
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('dz-header-scroll-lock');
    window.setTimeout(() => {
      const firstInvalid = this.modal.querySelector('[aria-invalid="true"]');
      const firstField = this.modal.querySelector('input:not([type="hidden"]), select, textarea');
      (firstInvalid || firstField || this.modalClose)?.focus();
    }, 40);
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.inert = true;
    document.body.classList.remove('dz-header-scroll-lock');
    this.lastFocusedElement?.focus?.();
  }

  getModalFocusables() {
    return [...this.modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.getClientRects().length > 0);
  }

  handleDocumentKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.modal?.classList.contains('is-open')) this.closeModal();
      else if (this.hasAttribute('data-drawer-open')) this.closeDrawer();
      else this.closeMenus();
      return;
    }

    if (event.key !== 'Tab' || !this.modal?.classList.contains('is-open')) return;
    const focusables = this.getModalFocusables();
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  handleDocumentPointerDown = (event) => {
    if (!this.contains(event.target)) this.closeMenus();
  };
}

if (!customElements.get('dezhou-header')) {
  customElements.define('dezhou-header', DezhouHeader);
}
